/**
 * Profile pictures. A picture when one is on file, initials otherwise.
 *
 * The URL carries the timestamp the picture last changed, so the browser can
 * cache it forever and still never show a stale one.
 */
import { useRef, useState, type ReactNode } from 'react';
import { api } from './api.js';
import { useToast } from './ui.js';

export function avatarUrl(userId: number | null | undefined, updatedAt: number | null | undefined): string | null {
  return userId && updatedAt ? `/api/users/${userId}/avatar?v=${updatedAt}` : null;
}

export function Avatar({ userId, updatedAt, initials, size = 28, tint }: {
  userId?: number | null;
  updatedAt?: number | null;
  initials: string;
  size?: number;
  /** Falls back to a tinted initials disc when there is no picture. */
  tint?: string;
}): JSX.Element {
  const url = avatarUrl(userId, updatedAt);
  const style: React.CSSProperties = {
    width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.38)),
    ...(tint && !url ? { background: tint } : {}),
  };
  if (!url) return <span className="avatar" style={style}>{initials}</span>;
  return <img className="avatar avatar--photo" style={style} src={url} alt="" />;
}

/** Resize to a centre-cropped square before upload, so what we store is small. */
async function toSquareDataUrl(file: File, edge = 256): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = edge;
  canvas.height = edge;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not read that image');
  ctx.drawImage(
    bitmap,
    (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side,
    0, 0, edge, edge,
  );
  bitmap.close();
  return canvas.toDataURL('image/jpeg', 0.86);
}

/**
 * A clickable avatar that opens the file picker. Used wherever a profile is
 * shown to someone allowed to change it.
 */
export function AvatarPicker({ userId, updatedAt, initials, size = 56, canEdit, onChanged, label }: {
  userId: number;
  updatedAt: number | null;
  initials: string;
  size?: number;
  canEdit: boolean;
  onChanged: () => void;
  label: string;
}): JSX.Element {
  const input = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await toSquareDataUrl(file);
      await api.put(`/users/${userId}/avatar`, { dataUrl });
      toast.ok('Picture updated');
      onChanged();
    } catch (err) {
      toast.error('Could not set that picture', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.del(`/users/${userId}/avatar`);
      toast.ok('Picture removed');
      onChanged();
    } catch (err) {
      toast.error('Could not remove it', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  if (!canEdit) return <Avatar userId={userId} updatedAt={updatedAt} initials={initials} size={size} />;

  return (
    <span className="avatar-edit">
      <button
        type="button"
        className="avatar-edit__btn"
        style={{ width: size, height: size }}
        disabled={busy}
        aria-label={updatedAt ? `Change ${label}'s picture` : `Add a picture for ${label}`}
        onClick={() => input.current?.click()}
      >
        <Avatar userId={userId} updatedAt={updatedAt} initials={initials} size={size} />
        <span className="avatar-edit__hint">{busy ? '…' : updatedAt ? 'Change' : 'Add'}</span>
      </button>
      {updatedAt && (
        <button type="button" className="btn btn--link" disabled={busy} onClick={remove}>Remove</button>
      )}
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={(e) => upload(e.target.files?.[0])}
      />
    </span>
  );
}

/**
 * The picture chosen during sign-up, before there is an account to attach it to.
 * Holds the data URL and hands it back so it can be uploaded once the account
 * exists.
 */
export function AvatarChooser({ value, onChange, initials, label }: {
  value: string | null;
  onChange: (dataUrl: string | null) => void;
  initials: string;
  label: string;
}): JSX.Element {
  const input = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const pick = async (file: File | undefined) => {
    if (!file) return;
    try {
      onChange(await toSquareDataUrl(file));
    } catch {
      toast.error('Could not read that image');
    }
    if (input.current) input.current.value = '';
  };

  return (
    <div className="row" style={{ gap: 14, flexWrap: 'nowrap', alignItems: 'center' }}>
      {value
        ? <img className="avatar avatar--photo" style={{ width: 64, height: 64 }} src={value} alt="" />
        : <span className="avatar" style={{ width: 64, height: 64, fontSize: 22 }}>{initials || '?'}</span>}
      <span className="stack" style={{ gap: 6, alignItems: 'flex-start' }}>
        <span className="row" style={{ gap: 8 }}>
          <button type="button" className="btn btn--sm" onClick={() => input.current?.click()}>
            {value ? 'Choose another' : 'Add a photo'}
          </button>
          {value && (
            <button type="button" className="btn btn--sm" onClick={() => onChange(null)}>Remove</button>
          )}
        </span>
        <span className="small muted">
          {value ? 'Cropped to a square.' : `Optional. ${label} will show initials until one is added.`}
        </span>
      </span>
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={(e) => pick(e.target.files?.[0])}
      />
    </div>
  );
}

export { toSquareDataUrl };

export type AvatarSubject = { userId?: number | null; avatarUpdatedAt?: number | null; initials: string };

export function avatarOf(subject: AvatarSubject, size?: number): ReactNode {
  return <Avatar userId={subject.userId} updatedAt={subject.avatarUpdatedAt} initials={subject.initials} size={size} />;
}
